import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MonthProjection, { computeMonthForecast } from "@/components/MonthProjection";

export default function Planning() {
  const [months, setMonths] = useState([]);
  const [bills, setBills] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [pending, setPending] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [account, setAccount] = useState({ current_balance: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [m, b, i, p, fs, a] = await Promise.all([
        api.get("/forecast/monthly?months=6"),
        api.get("/bills"),
        api.get("/incomes"),
        api.get("/pending-payments"),
        api.get("/funding-sources"),
        api.get("/account"),
      ]);
      setMonths(m.data);
      setBills(b.data);
      setIncomes(i.data);
      setPending(p.data);
      setFundingSources(fs.data);
      setAccount(a.data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="planning-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Monthly Outlook</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Your upcoming months at a glance</h1>
        <p className="text-[#59605C] mt-1 max-w-2xl">
          Every future month broken down by scheduled paychecks and bills owed. Each month shows <span className="font-medium text-[#2C302E]">Month net</span> (paychecks &minus; bills) — no fabricated starting balance is rolled forward because we can&rsquo;t know your untracked spending. This month lives on your Dashboard.
        </p>
      </div>

      {loading ? (
        <div className="text-[#59605C]">Loading…</div>
      ) : (
        <>
        <div className="grid xl:grid-cols-3 lg:grid-cols-2 gap-6" data-testid="planning-grid">
          {(() => {
            // Skip the CURRENT calendar month — the Dashboard already covers
            // it above with real-bank projection. This page is strictly
            // forward-looking.
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const currentIso = today.toISOString().slice(0, 7);
            const futureMonths = (months || []).filter(
              (m) => (m.month_start || "").slice(0, 7) !== currentIso
            );
            // Future months intentionally do NOT chain a starting balance
            // forward — we don't track every purchase so any carried-forward
            // EOM would be fiction. Each future month is shown as an
            // independent "Month net = incoming − bills" question.
            const netByIdx = [];
            const cards = futureMonths.map((m, idx) => {
              const monthStart = new Date(m.month_start + "T00:00:00");
              const monthEnd = new Date(m.month_end + "T00:00:00");
              const { incomingTotal, totalUnpaid } = computeMonthForecast({
                monthStart, monthEnd, isCurrentMonth: false,
                bills, incomes, pending, fundingSources,
                currentBalance: Number(account.current_balance || 0),
                startingBalance: 0,
              });
              netByIdx.push(incomingTotal - totalUnpaid);
              return (
                <MonthProjection
                  key={m.month_label}
                  monthLabel={m.month_label}
                  monthStart={monthStart}
                  monthEnd={monthEnd}
                  isCurrentMonth={false}
                  bills={bills}
                  incomes={incomes}
                  pending={pending}
                  fundingSources={fundingSources}
                  currentBalance={Number(account.current_balance || 0)}
                  startingBalance={0}
                  status={m.status}
                  extraPaycheck={!!m.extra_paycheck}
                  paycheckCount={m.paycheck_count || 0}
                  paycheckDates={m.paycheck_dates || []}
                  testId={`planning-month-${idx}`}
                />
              );
            });
            const cumulative = netByIdx.reduce((s, n) => s + n, 0);
            window.__cashbuddyPlanningCumulative = cumulative;
            return cards;
          })()}
        </div>
        <CumulativeNetStrip months={months} bills={bills} incomes={incomes}
          pending={pending} fundingSources={fundingSources}
          currentBalance={Number(account.current_balance || 0)} />
        </>
      )}
    </div>
  );
}

function CumulativeNetStrip({ months, bills, incomes, pending, fundingSources, currentBalance }) {
  if (!months || months.length <= 1) return null;
  // Skip current month (idx 0) — its EOM includes a real bank balance so it's
  // a different unit than future-month "net". Cumulative is a pure
  // "future paychecks vs future bills" summary.
  let cumulative = 0;
  for (let i = 1; i < months.length; i++) {
    const m = months[i];
    const monthStart = new Date(m.month_start + "T00:00:00");
    const monthEnd = new Date(m.month_end + "T00:00:00");
    const { incomingTotal, totalUnpaid } = computeMonthForecast({
      monthStart, monthEnd, isCurrentMonth: false,
      bills, incomes, pending, fundingSources, currentBalance, startingBalance: 0,
    });
    cumulative += incomingTotal - totalUnpaid;
  }
  const positive = cumulative >= 0;
  return (
    <div
      className={`mt-6 rounded-2xl border-2 px-5 py-4 flex items-center justify-between gap-4 flex-wrap ${positive ? "bg-[#DDE9D8] border-[#8FBF88]" : "bg-[#F5D9D1] border-[#E5B4A3]"}`}
      data-testid="planning-cumulative-net"
    >
      <div>
        <div className={`text-xs font-semibold uppercase tracking-widest ${positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}`}>
          {months.length - 1}-month cumulative net
        </div>
        <div className="text-sm text-[#59605C] mt-0.5">
          Sum of every future month&rsquo;s (paychecks &minus; bills). Not a bank balance — just how
          far ahead or behind those months add up.
        </div>
      </div>
      <div className={`font-heading text-3xl font-semibold ${positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}`}>
        {cumulative >= 0 ? "+" : ""}{cumulative.toLocaleString("en-US", { style: "currency", currency: "USD" })}
      </div>
    </div>
  );
}
